import { ChangeDetectionStrategy, Component, OnChanges, SimpleChanges, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { InputText } from 'primeng/inputtext';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { Select } from 'primeng/select';

import { Pessoa } from '@/shared/models/pessoa-info.model';
import { BotaoAcoesType } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { ActionChildComponent } from '@/shared/components/containers/action-child.component';
import { ConsultorioTelefoneService } from '@/core/api/sicom-api/registro/medico/consultorio-privado/consultorio-telefone.service';
import { ConsultorioTelefoneDeleteRequest, ConsultorioTelefoneInsertRequest, ConsultorioTelefoneRequest } from '@/core/api/sicom-api/registro/medico/consultorio-privado/consultorio-telefone.model';
import { ConsultorioPrivadoResponse } from '@/core/api/sicom-api/registro/medico/consultorio-privado/consultorio-privado.model';
import { ConsultorioPrivadoService } from '@/core/api/sicom-api/registro/medico/consultorio-privado/consultorio-privado.service';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';

import { CardContent } from '@/shared/components/card-content/card-content';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

@Component({
    selector: 'app-consultorio-telefone',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, ButtonModule, TooltipModule, CustomTableComponent, InputText, Select, ConfirmDialogModule, CardContent],
    providers: [ConfirmationService],
    templateUrl: './consultorio-telefone.html',
    styleUrl: './consultorio-telefone.scss'
})
/** Aba de telefones e endereço do consultório privado. */
export class ConsultorioTelefoneComponent extends ActionChildComponent implements OnChanges {
    private readonly telefoneService = inject(ConsultorioTelefoneService);
    private readonly consultorioService = inject(ConsultorioPrivadoService);
    private readonly messageService = inject(MessageService);
    private readonly confirmationService = inject(ConfirmationService);

    protected readonly roleConsultorioEditar = PERMISSOES.medicos.consultorio.editar;

    pessoa = input<Pessoa>();
    idConsultorio = input<number | null>(null);
    isEditing = false;

    tipoTelefoneOptions = [
        { label: 'Fixo', value: 'F' },
        { label: 'Celular', value: 'C' }
    ];

    novoTipoTelefone: 'F' | 'C' | null = null;
    novoDdd = '';
    novoTelefone = '';
    novoRamal = '';
    novaObservacao = '';

    telefones = signal<
        Array<{
            tipo: string;
            ddd: string;
            telefone: string;
            ramal?: string;
            observacao?: string;
            inTipoTelefone: string;
        }>
    >([]);

    loading = signal(false);
    erro = signal<string | null>(null);
    erroEndereco = signal<string | null>(null);
    enderecoConsultorio = signal({
        endereco: '',
        bairro: '',
        municipio: '',
        uf: '',
        cep: ''
    });

    tableMetadata: TableMetadata = {
        headers: {
            tipo: { type: 'text', value: 'Tipo', hidden: false, default: '' },
            ddd: { type: 'text', value: 'DDD', hidden: false, default: '' },
            telefone: { type: 'text', value: 'Telefone', hidden: false, default: '' },
            ramal: { type: 'text', value: 'Ramal', hidden: false, default: '' },
            observacao: { type: 'text', value: 'Observação', hidden: false, default: '' }
        },
        title: 'telefone',
        actions: {
            remover: {
                label: '',
                icon: 'pi pi-trash',
                color: 'danger',
                permission: this.roleConsultorioEditar,
                command: (item: { ddd?: string; telefone?: string; inTipoTelefone?: string }) => this.deletar(item)
            }
        }
    };

    actions: BotaoAcoesType & { useEdit: boolean } = {
        items: [
            {
                label: 'Adicionar',
                icon: 'pi pi-plus-circle',
                command: () => this.novo(),
                styleClass: 'bg-green-500',
                permission: this.roleConsultorioEditar
            }
        ],
        useEdit: false
    };

    override initComponent(): void {}

    override getDefinedActions(): BotaoAcoesType & { useEdit: boolean } {
        return this.actions;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['pessoa'] || changes['idConsultorio']) {
            const p = this.pessoa();
            const id = this.idConsultorio();
            if (p && typeof p.id === 'number' && !!p.uf && typeof id === 'number') {
                this.carregar({ id: p.id, uf: p.uf }, id);
            } else {
                this.limpar();
            }
        }
    }

    private carregar(p: Pessoa, idConsultorio: number): void {
        this.loading.set(true);
        this.erroEndereco.set(null);

        this.telefoneService.buscar({ id: p.id, uf: p.uf, idConsultorio }).subscribe({
            next: (lista: ConsultorioTelefoneRequest[]) => {
                this.preencher(lista ?? []);
                this.loading.set(false);
                this.erro.set(null);
            },
            error: () => {
                this.limpar();
                this.loading.set(false);
                this.erro.set('Não foi possível carregar os telefones.');
            }
        });

        this.consultorioService.buscar({ id: p.id, uf: p.uf, idConsultorio }).subscribe({
            next: (lista: ConsultorioPrivadoResponse[]) => {
                const consultorio = this.selecionarRegistro(lista, idConsultorio);
                this.preencherEndereco(consultorio);
                this.erroEndereco.set(null);
            },
            error: () => {
                this.preencherEndereco(null);
                this.erroEndereco.set('Não foi possível carregar o endereço do consultório.');
            }
        });
    }

    private preencher(lista: ConsultorioTelefoneRequest[]): void {
        const mapTipo = (codigo: string | undefined | null): string => {
            const c = (codigo ?? '').toString().toUpperCase();
            switch (c) {
                case 'F':
                    return 'Fixo';
                case 'C':
                    return 'Celular';
                case 'R':
                    return 'Residencial';
                case 'T':
                    return 'Trabalho';
                default:
                    return c || '';
            }
        };

        this.telefones.set(
            (lista || []).map((t) => ({
                tipo: mapTipo(t.inTipoTelefone),
                ddd: t.nuDdd ?? '',
                telefone: t.nuTelefone ?? '',
                ramal: t.nuRamal ?? '',
                observacao: t.txObservacao ?? '',
                inTipoTelefone: (t.inTipoTelefone ?? '').toString().toUpperCase()
            }))
        );
    }

    private limpar(): void {
        this.telefones.set([]);
        this.erro.set(null);
        this.erroEndereco.set(null);
        this.isEditing = false;
        this.resetFormularioTelefone();
        this.preencherEndereco(null);
    }

    private selecionarRegistro(lista: ConsultorioPrivadoResponse[] | null | undefined, idConsultorio: number): ConsultorioPrivadoResponse | null {
        if (!Array.isArray(lista) || lista.length === 0) return null;
        return lista.find((item) => item.idConsultorio === idConsultorio) ?? lista[0];
    }

    private preencherEndereco(consultorio: ConsultorioPrivadoResponse | null): void {
        this.enderecoConsultorio.set({
            endereco: consultorio?.txEndereco ?? '',
            bairro: consultorio?.nmBairro ?? '',
            municipio: consultorio?.dsMunicipio ?? '',
            uf: consultorio?.sgUf ?? '',
            cep: consultorio?.nuCep ?? ''
        });
    }

    novo(): void {
        this.isEditing = true;
    }

    cancelarCadastroTelefone(): void {
        this.isEditing = false;
        this.resetFormularioTelefone();
    }

    salvarTelefone(): void {
        const p = this.pessoa();
        const idConsultorio = this.idConsultorio();

        if (!p || p.id == null || !p.uf || typeof idConsultorio !== 'number') {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Dados do consultório não informados.' });
            return;
        }

        if (!this.novoTipoTelefone) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Selecione o tipo de telefone.' });
            return;
        }

        const ddd = this.apenasDigitos(this.novoDdd);
        const telefone = this.apenasDigitos(this.novoTelefone);
        const ramal = this.apenasDigitos(this.novoRamal);

        if (ddd.length !== 2) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'DDD inválido. Informe 2 dígitos.' });
            return;
        }

        if (telefone.length < 8 || telefone.length > 9) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Telefone inválido. Informe 8 ou 9 dígitos.' });
            return;
        }

        const body: ConsultorioTelefoneInsertRequest = {
            idConsultorio,
            nuTelefone: telefone,
            nuDdd: ddd,
            txObservacao: this.novaObservacao?.trim() || null,
            nuRamal: ramal || null,
            inTipoTelefone: this.novoTipoTelefone
        };

        this.loading.set(true);
        this.telefoneService.inserirTelefone({ id: p.id, uf: p.uf }, body).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Telefone inserido com sucesso.' });
                this.cancelarCadastroTelefone();
                this.carregar({ id: p.id, uf: p.uf }, idConsultorio);
            },
            // Sem toast local: o interceptor global já avisou. Fica só o `loading`, que o ramo de
            // sucesso desliga por outro caminho.
            error: () => this.loading.set(false)
        });
    }

    private resetFormularioTelefone(): void {
        this.novoTipoTelefone = null;
        this.novoDdd = '';
        this.novoTelefone = '';
        this.novoRamal = '';
        this.novaObservacao = '';
    }

    private apenasDigitos(valor: string | null | undefined): string {
        return (valor ?? '').replace(/\D+/g, '');
    }

    deletar(item: { ddd?: string; telefone?: string; inTipoTelefone?: string }): void {
        const p = this.pessoa();
        const idConsultorio = this.idConsultorio();

        if (!p || p.id == null || !p.uf || typeof idConsultorio !== 'number') {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Dados do consultório não informados.' });
            return;
        }

        const nuDdd = this.apenasDigitos(item?.ddd);
        const nuTelefone = this.apenasDigitos(item?.telefone);
        const inTipoTelefone = (item?.inTipoTelefone ?? '').toString().toUpperCase();

        if (!nuDdd || !nuTelefone || !inTipoTelefone) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Dados do telefone inválidos para exclusão.' });
            return;
        }

        const body: ConsultorioTelefoneDeleteRequest = {
            idConsultorio,
            nuTelefone,
            nuDdd,
            inTipoTelefone
        };

        this.confirmationService.confirm({
            header: 'Confirmação',
            icon: 'pi pi-exclamation-triangle',
            message: 'Deseja realmente excluir este telefone do consultório?',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => {
                this.loading.set(true);
                this.telefoneService.deletarTelefone({ id: p.id, uf: p.uf }, body).subscribe({
                    next: () => {
                        this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Telefone removido com sucesso.' });
                        this.carregar({ id: p.id, uf: p.uf }, idConsultorio);
                    },
                    // Sem toast local — ver acima.
                    error: () => this.loading.set(false)
                });
            }
        });
    }

    override updateActionsWithIndexes(actions: [MenuItem, number][]): void {}

    override toggleAction(action: MenuItem): MenuItem {
        return action;
    }
}
